import React, { useEffect } from 'react';
import { message, Skeleton } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
// Figma 14513:17620 — exact gift glyph from the mock (antd GiftOutlined looks different).
import { ReactComponent as GiftIcon } from 'app/assets/images/referralGift.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getReferralSummary } from 'app/store/reducers/referral.reducer';

const copyViaTextarea = (text: string): boolean => {
  const textarea = document.createElement('textarea');

  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);

  return copied;
};

export const ReferAFriendBlock: React.FC = () => {
  const dispatch = useAppDispatch();
  const { summary, summaryLoading } = useAppSelector((state) => state.referral);

  useEffect(() => {
    dispatch(getReferralSummary());
  }, []);

  const copyCode = () => {
    if (!summary?.referralCode) {
      return;
    }

    const code = summary.referralCode;
    // Ready-to-paste share message: the ?promoCode= link parks the code for guests and applies it
    // to the cart right after they authorize (PendingPromoCode.hooks).
    const shareText = `Use my MB Go! code ${code} to get a reward on your first order.\nTap to claim: ${window.location.origin}/?promoCode=${code}`;
    const onCopied = () => message.success('FriendCode copied!');
    const onFailed = () => message.error('Could not copy the code automatically — please copy it manually.');

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(shareText)
        .then(onCopied)
        .catch(() => {
          if (copyViaTextarea(shareText)) {
            onCopied();
          } else {
            onFailed();
          }
        });
    } else if (copyViaTextarea(shareText)) {
      // Non-secure context / old WebView: clipboard API is unavailable, fall back to execCommand.
      onCopied();
    } else {
      onFailed();
    }
  };

  // #1/#2: no active referral program → hide the whole section (nothing to earn/share).
  // No-orders case renders nothing here — the orange banner lives OUTSIDE the brown card
  // (ReferAFriendPlaceholder below), per Figma 14513:17682.
  if (summary && (!summary.programActive || !summary.canShare || !summary.referralCode)) {
    return null;
  }

  // Figma 14483:11827: the friend-code lives INSIDE the brown wallet-balance card —
  // dashed pill (code | copy icon), then a gift-icon share line with an orange accent.
  return (
    <div className="refer-a-friend ml-7 mr-7 pb-8">
      {summaryLoading && !summary ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        summary?.canShare &&
        summary?.referralCode && (
          <>
            <div className="refer-a-friend__code-pill">
              <span className="refer-a-friend__code" data-testid="referral-code">
                {summary.referralCode}
              </span>
              <span className="refer-a-friend__divider" />
              <CopyOutlined className="refer-a-friend__copy" onClick={copyCode} />
            </div>
            <div className="refer-a-friend__share-row">
              <GiftIcon className="refer-a-friend__gift-icon" />
              <span className="fs-xs">
                Share your code — friends get a reward on their first order, you earn{' '}
                <span className="color-orange-main font-weight-7">
                  {/* Figma 14483:11827: amount comes from the active promo via the summary. */}
                  {typeof summary.sharerBitecoinsAmount === 'number' ? `${summary.sharerBitecoinsAmount} bitecoins` : 'bitecoins'}
                </span>{' '}
                once they pick it up!
              </span>
            </div>
          </>
        )
      )}
    </div>
  );
};

/**
 * Figma 14513:17682: standalone orange banner shown BELOW the brown balance card until the
 * customer completes their first order. Reads the summary fetched by ReferAFriendBlock.
 */
export const ReferAFriendPlaceholder: React.FC = () => {
  const { summary } = useAppSelector((state) => state.referral);

  if (!summary || !summary.programActive || (summary.canShare && summary.referralCode)) {
    return null;
  }

  return (
    <div className="refer-a-friend__placeholder mt-4">
      <GiftIcon className="refer-a-friend__gift-icon" />
      <span className="fs-xs">
        Complete <span className="font-weight-7">1 order</span> on MB Go to get your FriendCode and earn even more rewards for
        sharing!
      </span>
    </div>
  );
};
